import { HttpClient } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Observable, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  HybridMetrics,
  HybridStructure,
  HybridTimeseries,
  NudgeResponse,
  RefreshResponse,
} from './hybrid-reporting.types';

export type Loadable<T> =
  | { status: 'idle' }
  | { status: 'loading'; briefId: string; previous: T | null }
  | { status: 'error'; briefId: string; previous: T | null }
  | { status: 'loaded'; briefId: string; data: T };

/** The current data for a brief, or the last good data while a reload is in flight. */
export function dataOf<T>(state: Loadable<T>): T | null {
  if (state.status === 'loaded') return state.data;
  if (state.status === 'idle') return null;
  return state.previous;
}

/**
 * Hybrid Reporting data, fetched in three independent requests so the page paints in stages:
 * structure (database only, fast) → metrics (both sources) → timeseries (daily history).
 * All three start together; each fills its own part of the page when it lands.
 */
@Injectable()
export class HybridReportingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.shout_api.url}/briefs`;

  readonly structure = signal<Loadable<HybridStructure>>({ status: 'idle' });
  readonly metrics = signal<Loadable<HybridMetrics>>({ status: 'idle' });
  readonly timeseries = signal<Loadable<HybridTimeseries>>({ status: 'idle' });

  load(briefId: string, opts: { force?: boolean } = {}): void {
    this.fetch(this.structure, briefId, 'structure', opts.force);
    this.fetch(this.metrics, briefId, 'metrics', opts.force);
    this.fetch(this.timeseries, briefId, 'timeseries', opts.force);
  }

  /** Reloads stats only (after a refresh finishes); the rows stay on screen. */
  reloadStats(briefId: string): void {
    this.fetch(this.metrics, briefId, 'metrics', true);
    this.fetch(this.timeseries, briefId, 'timeseries', true);
    this.fetch(this.structure, briefId, 'structure', true);
  }

  refresh(briefId: string): Observable<RefreshResponse> {
    return this.http.post<RefreshResponse>(`${this.base}/${briefId}/hybrid-reporting/refresh`, {}).pipe(take(1));
  }

  refreshStatus(briefId: string, taskNames: string[]): Observable<{ status: 'running' | 'complete' }> {
    return this.http
      .post<{ status: 'running' | 'complete' }>(`${this.base}/${briefId}/hybrid-reporting/refresh/status`, { taskNames })
      .pipe(take(1));
  }

  nudge(briefId: string, applicationId: string): Observable<NudgeResponse> {
    return this.http.post<NudgeResponse>(`${this.base}/${briefId}/hybrid-reporting/nudge/${applicationId}`, {}).pipe(take(1));
  }

  private fetch<T>(
    target: WritableSignal<Loadable<T>>,
    briefId: string,
    path: 'structure' | 'metrics' | 'timeseries',
    force = false,
  ): void {
    const current = target();
    const sameBrief = current.status !== 'idle' && current.briefId === briefId;
    if (!force && sameBrief && current.status !== 'error') return;
    const previous = sameBrief ? dataOf(current) : null;
    target.set({ status: 'loading', briefId, previous });
    this.http
      .get<T>(`${this.base}/${briefId}/hybrid-reporting/${path}`)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (this.briefIdOf(target()) === briefId) target.set({ status: 'loaded', briefId, data });
        },
        error: (err) => {
          console.error(`hybrid-reporting ${path} failed`, err);
          if (this.briefIdOf(target()) === briefId) target.set({ status: 'error', briefId, previous });
        },
      });
  }

  private briefIdOf(state: Loadable<unknown>): string | null {
    return state.status === 'idle' ? null : state.briefId;
  }
}
